import { useCallback, useState } from "react"

import { getPdfJs } from "@/lib/pdf-worker"

export interface PdfFile {
  id: string
  file: File
  name: string
  pageCount: number
  thumbnails: string[]
}

export function usePdfThumbnails() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generateThumbnails = useCallback(
    async (file: File): Promise<PdfFile> => {
      setLoading(true)
      setError(null)

      try {
        const pdfjsLib = await getPdfJs()
        const arrayBuffer = await file.arrayBuffer()
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
        const thumbnails: string[] = []

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i)
          const viewport = page.getViewport({ scale: 0.3 })

          const canvas = document.createElement("canvas")
          canvas.width = viewport.width
          canvas.height = viewport.height

          const context = canvas.getContext("2d")!
          await page.render({ canvas, canvasContext: context, viewport })
            .promise

          thumbnails.push(canvas.toDataURL("image/jpeg", 0.7))
        }

        return {
          id: crypto.randomUUID(),
          file,
          name: file.name,
          pageCount: pdf.numPages,
          thumbnails,
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load PDF"
        setError(message)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  return { generateThumbnails, loading, error }
}
